import { useEffect, useState } from "react";
import PortalLayout from "../../components/PortalLayout";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useI18n } from "../../i18n/LanguageContext";

/**
 * Introducer's anonymised referrals list.
 *
 * Displayed data (never PII):
 *   - Sequential label (Cliente #1, Cliente #2, ...)
 *   - Activation month (rounded — never exact date)
 *   - Active / other status
 *   - Contribution to AUM (principal sum of their active deposits)
 *
 * Names, emails, phone numbers, KYC documents — never shown.
 */
export default function IntroducerReferralsPage() {
  const { locale, t } = useI18n();
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/introducer/referrals")
      .then(({ data }) => setRows(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  const fmtEur = (n) =>
    new Intl.NumberFormat(locale === "en" ? "en-GB" : locale === "de" ? "de-DE" : locale === "no" ? "nb-NO" : "it-IT", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(Number(n || 0));

  const fmtMonth = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(d);
    } catch {
      return "—";
    }
  };

  const statusLabel = (s) => {
    if (s === "active") return t("intro.status_active");
    if (s === "pending_invite") return t("intro.status_pending");
    if (s === "suspended") return t("intro.status_suspended");
    return s || "—";
  };

  return (
    <PortalLayout>
      <section
        className="max-w-[1280px] mx-auto px-6 sm:px-8 pt-14 pb-8"
        data-testid="introducer-referrals-page"
      >
        <div className="br-fade-up">
          <div className="br-overline">{t("intro.referrals_overline")}</div>
          <h1 className="font-serif-display text-[clamp(2.4rem,4.5vw,3.6rem)] mt-3">
            {t("intro.referrals_heading_a")}{" "}
            <em>{t("intro.referrals_heading_em")}</em>
          </h1>
          <p className="text-[1.05rem] text-[color:var(--br-text)] mt-3 max-w-[720px]">
            {t("intro.referrals_intro")}
          </p>
        </div>

        {error && (
          <div className="mt-6 br-card p-4 text-[color:var(--br-red)]" data-testid="referrals-error">
            {error}
          </div>
        )}

        <div className="mt-10 br-card overflow-x-auto br-fade-up br-delay-1" data-testid="referrals-table-wrap">
          <table className="w-full text-left border-collapse" data-testid="referrals-table">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--br-line)" }}>
                <Th>{t("intro.col_ref")}</Th>
                <Th>{t("intro.col_activated")}</Th>
                <Th>{t("intro.col_status")}</Th>
                <Th align="right">{t("intro.col_principal")}</Th>
                <Th align="right">{t("intro.col_deposits")}</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-[color:var(--br-text-dim)] font-mono-fin text-xs tracking-[0.24em] uppercase">
                    {t("intro.referrals_loading")}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-[color:var(--br-text-dim)]" data-testid="referrals-empty">
                    {t("intro.referrals_empty")}
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={r.index}
                    className="border-b"
                    style={{ borderColor: "var(--br-line)" }}
                    data-testid={`referral-row-${r.index}`}
                  >
                    <Td className="font-serif-display italic text-[1.05rem] text-[color:var(--br-parchment)]">
                      {r.label}
                    </Td>
                    <Td className="font-mono-fin text-[color:var(--br-text-dim)]">
                      {fmtMonth(r.activated_at)}
                    </Td>
                    <Td className="font-mono-fin text-[0.78rem] tracking-[0.16em] uppercase" style={{ color: r.status === "active" ? "var(--br-bronze-soft)" : "var(--br-text-dim)" }}>
                      {statusLabel(r.status)}
                    </Td>
                    <Td align="right" className="font-mono-fin text-[color:var(--br-parchment)]">
                      {fmtEur(r.principal_eur)}
                    </Td>
                    <Td align="right" className="font-mono-fin text-[color:var(--br-text-dim)]">
                      {r.deposits_count}
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <p className="mt-6 text-[0.78rem] text-[color:var(--br-text-dim)] leading-relaxed max-w-[720px]" data-testid="privacy-footnote">
          {t("intro.referrals_privacy_footnote")}
        </p>
      </section>
    </PortalLayout>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th
      className="px-5 py-4 font-mono-fin text-[0.62rem] tracking-[0.22em] uppercase font-medium"
      style={{ color: "var(--br-text-dim)", textAlign: align }}
    >
      {children}
    </th>
  );
}

function Td({ children, align = "left", className = "", style }) {
  return (
    <td className={`px-5 py-5 text-[0.9rem] ${className}`} style={{ textAlign: align, ...style }}>
      {children}
    </td>
  );
}
